import { z } from 'zod';
import type { MapOptions, RequestParameters } from 'maplibre-gl';

/**
 * Basemap wiring for MapLibre.
 *
 * MapLibre can render Mapbox's styles, but it doesn't speak the `mapbox://`
 * protocol those styles use internally for their sprites, glyphs and vector
 * sources. `transformRequest` is the hook where we translate those references
 * into the equivalent public HTTPS endpoints and attach the access token.
 *
 * Every basemap comes in two versions: the Mapbox style to use when a token is
 * configured, and a keyless CARTO style to use when one isn't. A fresh clone
 * with no token still gets a real, colour basemap and a working switcher — only
 * satellite imagery, which has no keyless equivalent, drops out.
 */

const MAPBOX_API = 'https://api.mapbox.com';
const MAPBOX_PROTOCOL = 'mapbox://';
const CARTO_STYLES = 'https://basemaps.cartocdn.com/gl/';

/** Which basemap the map ended up using, so the UI can say so. */
export type BasemapProvider = 'mapbox' | 'carto';

export const basemapIdSchema = z.enum(['terrain', 'satellite', 'dark']);
export type BasemapId = z.infer<typeof basemapIdSchema>;

interface BasemapDefinition {
  label: string;
  /** One line in the switcher, so the choice isn't three unexplained words. */
  hint: string;
  /** `{user}/{styleId}` on Mapbox. */
  mapboxStyle: string;
  /** Keyless equivalent, or null where none exists (satellite imagery). */
  cartoStyle: string | null;
  /**
   * How hard to dim the basemap under the radar, 0–1.
   *
   * A colour basemap and the radar ramp compete for the same attention: green
   * precipitation over green landcover is genuinely hard to read. Rather than
   * give up the colour, each basemap carries a default dim that pushes it back
   * behind the data. Viewers can override it — some want the map, some want
   * the weather.
   */
  dim: number;
  /**
   * Opacity of the no-coverage scrim over this basemap, 0–1.
   *
   * The mask is opaque black where there is no radar, so how heavy it reads
   * depends entirely on what's underneath. The 0.4 the scrim was built with was
   * tuned against the dark basemap; over a colour basemap that is already being
   * dimmed, the same value stacks into a black hole. A viewer in an uncovered
   * area still has to be able to navigate.
   */
  coverageScrim: number;
}

/**
 * The basemaps on offer, in switcher order.
 *
 * Terrain leads: a weather map is read against land, coast and relief, and a
 * grey monochrome basemap makes every radar echo look like it's floating in
 * nothing.
 */
const BASEMAPS: Record<BasemapId, BasemapDefinition> = {
  terrain: {
    label: 'Terrain',
    hint: 'Land, water and relief in colour',
    mapboxStyle: 'mapbox/outdoors-v12',
    cartoStyle: `${CARTO_STYLES}voyager-gl-style/style.json`,
    dim: 0.34,
    coverageScrim: 0.3,
  },
  satellite: {
    label: 'Satellite',
    hint: 'Imagery with roads and labels',
    mapboxStyle: 'mapbox/satellite-streets-v12',
    // No keyless imagery source we can use within its licence terms.
    cartoStyle: null,
    dim: 0.28,
    coverageScrim: 0.3,
  },
  dark: {
    label: 'Dark',
    hint: 'Maximum contrast for the radar',
    mapboxStyle: 'mapbox/dark-v11',
    cartoStyle: `${CARTO_STYLES}dark-matter-gl-style/style.json`,
    dim: 0,
    coverageScrim: 0.4,
  },
};

/** Opens on a colour map — the whole point of the basemap being there. */
export const DEFAULT_BASEMAP_ID: BasemapId = 'terrain';

export interface BasemapOption {
  id: BasemapId;
  label: string;
  hint: string;
}

/**
 * The basemaps a given token can actually load, for rendering the switcher.
 * Without a token, satellite has no keyless stand-in and is left out rather
 * than offered and then silently substituted.
 */
export function availableBasemaps(mapboxToken: string): BasemapOption[] {
  return (Object.keys(BASEMAPS) as BasemapId[])
    .filter((id) => Boolean(mapboxToken) || BASEMAPS[id].cartoStyle !== null)
    .map((id) => ({ id, label: BASEMAPS[id].label, hint: BASEMAPS[id].hint }));
}

/**
 * The dim and scrim values a basemap wants, without resolving a provider.
 *
 * Switching basemap re-seeds the dim slider from here: the right amount of
 * dimming is a property of what's underneath, so carrying a value tuned for
 * satellite imagery over to the dark basemap would just look like a bug.
 */
export function basemapDefaults(id: BasemapId): { dim: number; coverageScrim: number } {
  const { dim, coverageScrim } = BASEMAPS[id] ?? BASEMAPS[DEFAULT_BASEMAP_ID];
  return { dim, coverageScrim };
}

export interface Basemap {
  id: BasemapId;
  provider: BasemapProvider;
  /** A style URL — both providers are loaded remotely rather than inlined. */
  style: string;
  /** Default dim to apply under the radar for this basemap, 0–1. */
  dim: number;
  /** Opacity the no-coverage scrim should use over this basemap, 0–1. */
  coverageScrim: number;
  /**
   * Whether MapLibre should validate the style against its spec.
   *
   * Off for Mapbox, whose styles carry Mapbox-only top-level keys (`fog`,
   * `projection`, `owner`, `draft`, …). MapLibre's validator treats those as
   * unknown properties and refuses to load the style; the renderer itself
   * ignores them happily.
   */
  validateStyle: boolean;
  /** Undefined for the fallback, which needs no request rewriting. */
  transformRequest?: (url: string, resourceType?: string) => RequestParameters | undefined;
}

export function resolveBasemap(mapboxToken: string, id: BasemapId = DEFAULT_BASEMAP_ID): Basemap {
  const definition = BASEMAPS[id] ?? BASEMAPS[DEFAULT_BASEMAP_ID];

  if (!mapboxToken) {
    // Asking for satellite without a token lands on the default rather than a
    // blank map — `availableBasemaps` hides it, but a stale URL or a stored
    // preference can still name it.
    const keyless = definition.cartoStyle ? definition : BASEMAPS[DEFAULT_BASEMAP_ID];

    return {
      id: definition.cartoStyle ? id : DEFAULT_BASEMAP_ID,
      provider: 'carto',
      style: keyless.cartoStyle as string,
      dim: keyless.dim,
      coverageScrim: keyless.coverageScrim,
      validateStyle: true,
    };
  }

  return {
    id,
    provider: 'mapbox',
    dim: definition.dim,
    coverageScrim: definition.coverageScrim,
    validateStyle: false,
    style: withToken(`${MAPBOX_API}/styles/v1/${definition.mapboxStyle}`, mapboxToken),
    transformRequest: (url) => {
      if (url.startsWith(MAPBOX_PROTOCOL)) {
        return { url: rewriteMapboxUrl(url, mapboxToken) };
      }
      // Style-relative URLs Mapbox returns already-expanded still need the token.
      if (url.startsWith(MAPBOX_API)) {
        return { url: withToken(url, mapboxToken) };
      }
      return undefined;
    },
  };
}

/**
 * Translates one `mapbox://` reference into its public HTTPS equivalent.
 *
 * The four forms that appear inside a Mapbox style:
 *   mapbox://styles/{user}/{id}                  -> /styles/v1/{user}/{id}
 *   mapbox://sprites/{user}/{id}{@2x}{.png|.json} -> /styles/v1/{user}/{id}/sprite{suffix}
 *   mapbox://fonts/{user}/{stack}/{range}.pbf    -> /fonts/v1/{user}/{stack}/{range}.pbf
 *   mapbox://{tileset-id}[,{tileset-id}]         -> /v4/{ids}.json?secure
 */
export function rewriteMapboxUrl(url: string, token: string): string {
  const rest = url.slice(MAPBOX_PROTOCOL.length);

  if (rest.startsWith('styles/')) {
    return withToken(`${MAPBOX_API}/styles/v1/${rest.slice('styles/'.length)}`, token);
  }

  if (rest.startsWith('fonts/')) {
    return withToken(`${MAPBOX_API}/fonts/v1/${rest.slice('fonts/'.length)}`, token);
  }

  if (rest.startsWith('sprites/')) {
    // Split `{user}/{styleId}` from the `@2x.png` / `.json` suffix MapLibre appends.
    const match = /^([^/]+)\/([^/@.]+)(.*)$/.exec(rest.slice('sprites/'.length));
    if (!match) return withToken(`${MAPBOX_API}/styles/v1/${rest.slice(8)}`, token);

    const [, user, styleId, suffix] = match;
    return withToken(`${MAPBOX_API}/styles/v1/${user}/${styleId}/sprite${suffix}`, token);
  }

  // Anything else is a tileset id (or comma-separated list) resolving to TileJSON.
  // `secure` is a valueless flag, so it's appended rather than set via searchParams.
  return `${withToken(`${MAPBOX_API}/v4/${rest}.json`, token)}&secure`;
}

/**
 * The style handed to MapLibre: a URL for styles that need no preprocessing, or
 * a parsed style document for those that do.
 */
export type BasemapStyle = NonNullable<MapOptions['style']>;

/**
 * Resolves the style MapLibre should load.
 *
 * Mapbox styles need one fix-up first. `transformRequest` only runs on the main
 * thread, but MapLibre fetches **glyphs from its worker**, so a
 * `mapbox://fonts/...` glyph template never gets rewritten and every font
 * request fails silently — the map renders geometry with no labels at all, and
 * never reports itself fully loaded. Rewriting the template in the style
 * document is the only place that reaches the worker.
 *
 * Sprites and sources are left as `mapbox://` on purpose: both are fetched on
 * the main thread, where `transformRequest` handles them, and pre-rewriting the
 * sprite would append a query string that MapLibre then has to build `.json`
 * and `@2x.png` around.
 */
export async function loadBasemapStyle(
  basemap: Basemap,
  mapboxToken: string,
): Promise<BasemapStyle> {
  if (basemap.provider !== 'mapbox') return basemap.style;

  const response = await fetch(basemap.style);
  if (!response.ok) {
    throw new Error(`Mapbox style request failed with ${response.status}`);
  }

  const style = (await response.json()) as Record<string, unknown>;

  if (typeof style.glyphs === 'string' && style.glyphs.startsWith(MAPBOX_PROTOCOL)) {
    style.glyphs = rewriteMapboxUrl(style.glyphs, mapboxToken);
  }

  // The document is a Mapbox style, which carries keys MapLibre's own
  // StyleSpecification type doesn't model — hence the cast rather than a parse.
  return style as unknown as BasemapStyle;
}

function withToken(url: string, token: string): string {
  if (url.includes('access_token=')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`;
}
